import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";

import { changePassword } from "../api/auth";
import { useAuth } from "../auth/AuthProvider";
import { explainError } from "../api/client";

interface FormValues {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export function ChangePassword() {
  const { me, refreshMe } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState, watch } = useForm<FormValues>({
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });
  const [serverError, setServerError] = useState<string | null>(null);

  const newPwd = watch("new_password");

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);
    if (values.new_password !== values.confirm_password) {
      setServerError("New password and confirmation do not match.");
      return;
    }
    try {
      await changePassword({
        current_password: values.current_password,
        new_password: values.new_password,
      });
      // After change-password the backend revokes all tokens. Force re-login.
      // (refreshMe would 401; the interceptor will redirect us cleanly.)
      try {
        await refreshMe();
      } catch {
        /* expected */
      }
      navigate("/login", { replace: true });
    } catch (e) {
      setServerError(explainError(e));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center paper-grain px-6 py-12">
      <div className="w-full max-w-[520px] animate-fade-up">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[var(--accent)] mb-3">
            <ShieldCheck size={16} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight">
            Set your password
          </h1>
          {me && (
            <p className="mt-3 font-sans text-[14px] text-[var(--fg-muted)]">
              Welcome, <span className="font-display italic">{me.username}</span>. Choose a new password to continue.
            </p>
          )}
        </div>

        <div className="np-card shadow-plate">
          <div className="px-6 py-3 border-b"
               style={{ borderColor: "var(--rule)" }}>
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)]">
              Change Password
            </span>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="px-6 py-7 flex flex-col gap-6"
            noValidate
          >
            <label className="flex flex-col gap-2">
              <span className="np-label">Current password</span>
              <input
                type="password"
                className="np-input font-mono"
                autoComplete="current-password"
                autoFocus
                {...register("current_password", { required: true })}
              />
              {formState.errors.current_password && (
                <span className="np-error">Required.</span>
              )}
            </label>

            <label className="flex flex-col gap-2">
              <span className="np-label">New password</span>
              <input
                type="password"
                className="np-input font-mono"
                autoComplete="new-password"
                {...register("new_password", { required: true, minLength: 10 })}
              />
              <span className="font-mono text-[10px] text-[var(--fg-faint)]">
                Minimum 10 characters.
              </span>
              {formState.errors.new_password && (
                <span className="np-error">
                  Must be at least 10 characters.
                </span>
              )}
            </label>

            <label className="flex flex-col gap-2">
              <span className="np-label">Confirm new password</span>
              <input
                type="password"
                className="np-input font-mono"
                autoComplete="new-password"
                {...register("confirm_password", {
                  required: true,
                  validate: (v) => v === newPwd || "Does not match",
                })}
              />
              {formState.errors.confirm_password && (
                <span className="np-error">Confirmation must match.</span>
              )}
            </label>

            {serverError && (
              <div className="np-banner" role="alert">
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={formState.isSubmitting}
              className="np-btn w-full"
            >
              {formState.isSubmitting ? "Saving…" : "Save and sign in again"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
